import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { SERVICES } from "@/data/content";

const SERVICES_BG =
  "https://static.prod-images.emergentagent.com/jobs/371a705d-1e9e-4b80-8780-1f565747a945/images/6bc26c3485e6316eb5a3e8b470f5a57a1c2a8db22dfffac2206fafe053ad3f7a.png";

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="IT Consulting, Cybersecurity, AI & Digital Engineering Services | Notion Minds Systems"
        description="Seven practices, one delivery standard: AI strategy & feasibility, IT consulting & digital engineering, cybersecurity & resilience, OT & industrial cybersecurity, ODC & managed delivery, quality engineering, and GIS, geospatial, mobile mapping & drone solutions."
        canonical="https://notionminds.sa/services"
      />
      <section className="relative pt-24 pb-20 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-30">
          <img src={SERVICES_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <p className="font-mono-accent text-xs text-[#d4af37] mb-5">[ Services ]</p>
          <h1 className="font-display text-5xl lg:text-7xl font-light leading-[1.05] max-w-4xl">
            Seven practices.<br />
            <span className="text-gradient-gold font-semibold">One delivery standard.</span>
          </h1>
          <p className="mt-8 text-white/65 text-lg max-w-2xl leading-relaxed">
            Each practice is led by senior practitioners with direct enterprise
            experience across Saudi Arabia, Oman and Qatar — and shaped by a unified delivery operating model.
          </p>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 space-y-20">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          const flip = i % 2 === 1;
          return (
            <motion.article
              key={s.id}
              id={s.id}
              data-testid={`services-section-${s.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
            >
              <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                <div className="sticky top-28">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-mono-accent text-xs text-[#d4af37]">
                      / 0{i + 1}
                    </span>
                    <span className="h-px w-12 bg-[#d4af37]" />
                  </div>
                  <div className="w-16 h-16 border border-[#d4af37]/50 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#d4af37]" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-display text-3xl lg:text-5xl font-light leading-tight">
                    {s.title}
                  </h2>
                </div>
              </div>
              <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                <p className="text-white/70 text-lg leading-relaxed mb-10 border-l-2 border-[#d4af37] pl-6">
                  {s.description}
                </p>
                <p className="font-mono-accent text-xs text-white/50 mb-5">
                  What we deliver
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {s.bullets.map((b) => {
                    const [lead, ...rest] = b.split(" — ");
                    const hasLead = rest.length > 0;
                    return (
                      <li key={b} className="flex items-start gap-3 p-4 bg-[#0d0d12] border border-[#1c1c22] hover:border-[#d4af37]/40 transition-colors">
                        <Check className="w-4 h-4 text-[#d4af37] mt-1 shrink-0" />
                        <span className="text-sm text-white/80 leading-relaxed">
                          {hasLead && (
                            <span className="block font-display text-white font-medium mb-1">{lead}</span>
                          )}
                          {hasLead ? rest.join(" — ") : lead}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {s.footnote && (
                  <p className="text-white/45 text-sm leading-relaxed mb-10 border-l-2 border-[#d4af37]/40 pl-5 italic" data-testid={`service-footnote-${s.id}`}>
                    {s.footnote}
                  </p>
                )}
                {!s.footnote && <div className="mb-4" />}
                <Link
                  to="/contact"
                  data-testid={`service-detail-cta-${s.id}`}
                  state={{ service: s.title }}
                  className="inline-flex items-center gap-2 text-[#d4af37] hover:gap-3 transition-all border-b border-[#d4af37] pb-1"
                >
                  Engage on {s.title} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.article>
          );
        })}

        <div
          className="mt-4 border border-[#24242a] p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          data-testid="services-partner-cta"
        >
          <div>
            <p className="font-mono-accent text-xs text-[#d4af37] mb-3">Partnership opportunity</p>
            <p className="text-white/70 max-w-2xl text-sm leading-relaxed">
              Have strong relationships in the Saudi enterprise technology market?
              Explore our{" "}
              <Link
                to="/careers/business-development-partner-saudi-arabia"
                className="text-[#d4af37] hover:underline"
                data-testid="services-bd-partner-link"
              >
                Independent Business Development Partner — Saudi Arabia
              </Link>{" "}
              program.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
